import { type FamilyState } from "#customTypes/composedTypes/standoutTypes"

/**
 * Read-time backfill for standout moments written before quick stories.
 * Pure: no Firestore. Missing familyState, source, media, and media ids are
 * filled in. Every other field is left as stored.
 */

export const familyReactionKey = (
  mobileUid: string,
  elementId: string
): string => `${mobileUid}|${elementId}`

export const isSharedForFamilyState = (state: FamilyState): boolean =>
  state === "approved" || state === "sent"

/** sharedBy value the content sweep writes. Unshare must not erase it. */
export const CONTENT_SWEEP_ACTOR = "contentSweep"

/**
 * Pending leaves sharedAt/sharedBy untouched. Approving a row the sweep
 * already sent also leaves those stamps, so the 30-day family budget still
 * sees the send. A first share stamps the acting user.
 */
export const familySharePatch = (
  state: FamilyState,
  userId: string,
  nowIso: string,
  existingSharedBy?: string | null
):
  | { familyState: FamilyState; isShared: boolean }
  | {
      familyState: FamilyState
      isShared: true
      sharedAt: string
      sharedBy: string
    } => {
  const isShared = isSharedForFamilyState(state)
  if (!isShared || existingSharedBy === CONTENT_SWEEP_ACTOR) {
    return { familyState: state, isShared }
  }
  return { familyState: state, isShared, sharedAt: nowIso, sharedBy: userId }
}

export const NO_CLASS_SESSION_LABEL = "No class session — can't be delivered"

export const deriveFamilyState = (moment: {
  isShared?: boolean
  familyState?: FamilyState
}): FamilyState =>
  moment.familyState ?? (moment.isShared ? "approved" : "pending")

/**
 * A history message counts as shared when the family already has it, or
 * it is approved and waiting to send. Secrets and don't-share notes do
 * not. Pending is not shared, even if a legacy `isShared` flag is still set.
 */
export const isHistoryMessageShared = (moment: {
  category?: string | null
  dontShare?: boolean | null
  familyState?: FamilyState | null
  isShared?: boolean | null
}): boolean => {
  if (moment.category === "secret" || moment.dontShare === true) return false
  if (moment.familyState === "pending") return false
  if (moment.familyState === "approved" || moment.familyState === "sent") {
    return true
  }
  return moment.isShared === true
}

/** Which sentence parents see when a moment has both a note and a generated one. */
export type FamilyNoteSource = "note" | "generatedNote"

/**
 * Parents get one sentence. When both the teacher's note and the generated
 * sentence exist, the generated sentence is used only if the teacher picked
 * it. A missing pick means the teacher's note. An empty generated pick does
 * not fall back to the note. When there is no teacher note, the generated
 * sentence is the only text, so that is what parents see.
 */
export const parentShareText = (moment: {
  note?: string | null
  generatedNote?: string | null
  familyNoteSource?: string | null
}): string => {
  const note = moment.note?.trim() ?? ""
  const generated = moment.generatedNote?.trim() ?? ""
  if (moment.familyNoteSource === "generatedNote") return generated
  if (note) return note
  return generated
}

export type SentNoteFields = {
  note?: string | null
  generatedNote?: string | null
  familyNoteSource?: string | null
  sentNoteSource?: FamilyNoteSource | null
  sentNoteEdited?: boolean | null
}

/**
 * The sentence a share would send. Null when there is no text note, so a
 * photo or Story Time share does not enter SUN or SMN.
 */
export const sentNoteSourceFor = (
  moment: SentNoteFields
): FamilyNoteSource | null => {
  if (!parentShareText(moment)) return null
  if (moment.familyNoteSource === "generatedNote" || !moment.note?.trim()) {
    return "generatedNote"
  }
  return "note"
}

/**
 * Written onto the moment when a text note is shared. Keeps a sticky edit.
 * Empty for a share that has no sentence.
 */
export const sentNoteSharePatch = (
  moment: SentNoteFields
):
  | { sentNoteSource: FamilyNoteSource; sentNoteEdited?: true }
  | Record<string, never> => {
  const sentNoteSource = sentNoteSourceFor(moment)
  if (!sentNoteSource) return {}
  if (moment.sentNoteEdited === true) {
    return { sentNoteSource, sentNoteEdited: true }
  }
  return { sentNoteSource }
}

/**
 * The shared sentence changed. Only after a share, and never cleared.
 * A generated sentence stays SMN from sentNoteSource, so this is for a
 * teacher note that was changed, or for switching which sentence is shared.
 */
export const markSentNoteEdited = (
  moment: SentNoteFields,
  sharedNow: boolean
): { sentNoteEdited: true } | Record<string, never> => {
  if (!sharedNow && moment.sentNoteSource == null) return {}
  return { sentNoteEdited: true }
}

/** Editing the teacher note changes the shared sentence only when that note is the one that was shared. */
export const markSentNoteEditedForNote = (
  moment: SentNoteFields,
  sharedNow: boolean
): { sentNoteEdited: true } | Record<string, never> => {
  if (!sharedNow && moment.sentNoteSource == null) return {}
  const source = moment.sentNoteSource ?? sentNoteSourceFor(moment)
  if (source !== "note") return {}
  return { sentNoteEdited: true }
}

/** Regenerating changes the shared sentence only when parents were sent the generated one. */
export const markSentNoteEditedForGenerate = (
  moment: SentNoteFields,
  sharedNow: boolean
): { sentNoteEdited: true } | Record<string, never> => {
  const source =
    moment.sentNoteSource ?? (sharedNow ? sentNoteSourceFor(moment) : null)
  if (source !== "generatedNote") return {}
  return { sentNoteEdited: true }
}

/**
 * SUN is a shared teacher note nobody changed. SMN is a shared generated
 * sentence, or any shared note changed after that share. Null is not a
 * shared sentence yet: a written-but-unshared note stays in the SUN/SMN
 * denominator and in neither numerator.
 */
export const sharedNoteKind = (
  moment: SentNoteFields
): "sun" | "smn" | null => {
  if (!moment.sentNoteSource) return null
  if (
    moment.sentNoteEdited === true ||
    moment.sentNoteSource === "generatedNote"
  ) {
    return "smn"
  }
  return "sun"
}

const hasMediaId = (entry: Record<string, unknown>): boolean =>
  typeof entry.id === "string" && entry.id.length > 0

const backfillMedia = (media: unknown): unknown[] => {
  if (!Array.isArray(media)) return []
  return media.map((entry) => {
    if (typeof entry !== "object" || entry === null || Array.isArray(entry)) {
      return entry
    }
    const record = entry as Record<string, unknown>
    if (hasMediaId(record)) return entry
    return { ...record, id: globalThis.crypto.randomUUID() }
  })
}

export const migrateStandoutMoment = <T extends Record<string, unknown>>(
  raw: T
): T => {
  const familyState =
    raw.familyState == null
      ? deriveFamilyState({ isShared: raw.isShared === true })
      : raw.familyState
  const source = raw.source == null ? "jarvis" : raw.source

  return {
    ...raw,
    familyState,
    source,
    media: backfillMedia(raw.media),
  } as T
}
